import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

// Legend crop modal: native PDF rendering with original raster crop coordinates.
// Saves AABB of the polygon (server crops that region for OCR) plus polygon points.
export default class extends Controller {
  static targets = [
    "stage",
    "viewport",
    "img",
    "svg",
    "polyPath",
    "polyLine",
    "polyDots",
    "status",
    "saveBtn",
    "resetBtn",
    "undoBtn",
    "lazyThumb",
    "modal",
    "modalTitle",
    "modalSub",
    "zoomLabel",
    "classifyModal"
  ]

  static values = {
    page: Number,
    pageWidth: Number,
    pageHeight: Number,
    updateUrl: String,
    pageRasterUrl: String,
    cropUrl: String,
    existingBbox: Object,
    existingPolygon: Array
  }

  connect() {
    this._modalOpen = false
    this._saving = false
    this._beforeStream = (event) => this.beforeCatalogStream(event)
    document.addEventListener("turbo:before-stream-render", this._beforeStream)
    this._points = [] // page-pixel {x,y}
    this._closed = false
    this._scale = 1
    this._panX = 0
    this._panY = 0
    this._panning = false
    this._panStart = null
    this._spaceDown = false
    this._dragVertex = -1
    this._activePageCard = null
    // Only the opened page has a display preview in flight.
    this._fullRasterGen = 0
    this._imageReady = false
    // Panel thumbs: observe only. Do not materialize every open card on connect.
    window.requestAnimationFrame(() => this._observeCatalogThumbs())
  }

  disconnect() {
    document.removeEventListener("turbo:before-stream-render", this._beforeStream)
    this._fullRasterGen += 1
    this._dropModalRaster()
    this._unlockBody()
    this._unbindWindow()
    this._unbindPinch()
    this._thumbObserver?.disconnect()
  }

  // Crop & edit button on a page card — binds page context then opens ONE modal.
  openPage(event) {
    event?.preventDefault?.()
    const el = event?.currentTarget
    if (!el || !this.hasModalTarget) return

    const page = Number(el.dataset.page || 0)
    if (!page) return

    this._cropId = el.dataset.cropId || "new"
    this.pageValue = page
    this.pageWidthValue = Number(el.dataset.pageWidth || 0)
    this.pageHeightValue = Number(el.dataset.pageHeight || 0)
    // Whole-page editor preview, separate from the cropped card thumbnail.
    const card = el.closest(".cd-catalog-page")
    this.pageRasterUrlValue =
      el.dataset.fullRasterUrl ||
      el.dataset.pageRasterUrl ||
      card?.dataset?.fullRasterUrl ||
      card?.dataset?.pageRasterUrl ||
      ""
    this.cropUrlValue = el.dataset.cropUrl || card?.dataset?.cropUrl || ""
    this._activePageCard = card

    let bbox = {}
    let poly = []
    try {
      bbox = JSON.parse(el.dataset.existingBbox || "{}")
    } catch (_e) {
      bbox = {}
    }
    try {
      poly = JSON.parse(el.dataset.existingPolygon || "[]")
    } catch (_e) {
      poly = []
    }
    this._loadGeometry(bbox, poly)

    const title = el.dataset.sheetTitle || `Page ${page}`
    if (this.hasModalTitleTarget) {
      this.modalTitleTarget.textContent = `Crop legend · p.${page}`
    }
    if (this.hasModalSubTarget) {
      this.modalSubTarget.textContent = title
    }

    this.openModal()
  }

  // Legacy name — open currently bound page (or no-op).
  openModal(event) {
    event?.preventDefault?.()
    if (!this.hasModalTarget) return
    if (!this.pageValue) {
      this._setStatus("Pick a legend page first.")
      return
    }

    this.modalTarget.hidden = false
    this._modalOpen = true
    document.body.classList.add("cd-legend-modal-open")
    this._bindWindow()
    this._bindPinch()

    this.startEdit()
    this._setStatus("Loading native PDF…")
    // Only a whole-page editor preview has the coordinate frame used by crops.
    // Start loading last so an immediate error cannot be replaced by loading text.
    this._loadFullRasterOneAtATime(this._fullRasterUrl())

  }

  openClassifyModal(event) {
    event?.preventDefault?.()
    if (!this.hasClassifyModalTarget) return
    this.classifyModalTarget.hidden = false
    document.body.classList.add("cd-legend-modal-open")
  }

  closeClassifyModal(event) {
    event?.preventDefault?.()
    if (!this.hasClassifyModalTarget) return
    this.classifyModalTarget.hidden = true
    if (!this._modalOpen) document.body.classList.remove("cd-legend-modal-open")
  }

  closeModal(event) {
    event?.preventDefault?.()
    if (!this.hasModalTarget) return
    this.modalTarget.hidden = true
    this._modalOpen = false
    this._panning = false
    this._dragVertex = -1
    this._pinch = null
    this._unbindBody()
    this._unbindWindow()
    this._unbindPinch()
    // Invalidate in-flight decode + drop full-res from memory until next open.
    this._fullRasterGen += 1
    this._dropModalRaster()
    if (this._deferredCatalogStream) {
      const html = this._deferredCatalogStream
      this._deferredCatalogStream = null
      Turbo.renderStreamMessage(html)
    }
  }

  beforeCatalogStream(event) {
    const stream = event.detail?.newStream
    const target = stream?.getAttribute("target")
    if (!target || (target !== this.element.id && !target.startsWith("catalog_page_"))) return
    if (event.defaultPrevented) return
    if (target === this.element.id && this._modalOpen) {
      // Do not destroy an in-progress crop when a background update arrives.
      event.preventDefault()
      this._deferredCatalogStream = stream.outerHTML
      return
    }
    const openIds = Array.from(this.element.querySelectorAll(".cd-catalog-page-details[open]"))
      .map((details) => details.closest(".cd-catalog-page")?.id).filter(Boolean)
    const render = event.detail.render
    event.detail.render = async (element) => {
      await render(element)
      openIds.forEach((id) => {
        const details = document.getElementById(id)?.querySelector("details")
        if (details) details.open = true
      })
    }
  }

  // Load panel thumbnail only when a page <details> is opened.
  onPageDetailsToggle(event) {
    const details = event?.currentTarget
    if (!details?.open) return
    const img = details.querySelector("img.cd-catalog-preview-img[data-src]")
    if (img) this._queueThumb(img)
  }

  _observeCatalogThumbs() {
    if (typeof IntersectionObserver === "undefined") return
    this._thumbObserver?.disconnect()
    this._thumbQueue = []
    this._thumbActive = 0
    this._thumbMax = 2
    this._thumbObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return
          const img = entry.target
          const details = img.closest("details")
          if (details && !details.open) return
          this._queueThumb(img)
          this._thumbObserver.unobserve(img)
        })
      },
      { root: null, rootMargin: "40px", threshold: 0.01 }
    )
    this.element.querySelectorAll("img.cd-catalog-preview-img[data-src]").forEach((img) => {
      const details = img.closest("details")
      if (details && !details.open) return
      this._thumbObserver.observe(img)
    })
  }

  _queueThumb(img) {
    if (!img || img.dataset.loaded === "1" || img.dataset.queued === "1") return
    img.dataset.queued = "1"
    this._thumbQueue.push(img)
    this._drainThumbs()
  }

  _drainThumbs() {
    while (this._thumbActive < this._thumbMax && this._thumbQueue.length) {
      const img = this._thumbQueue.shift()
      if (!img.isConnected || img.dataset.loaded === "1") continue
      this._thumbActive += 1
      const done = () => {
        img.removeEventListener("load", done)
        img.removeEventListener("error", done)
        delete img.dataset.queued
        this._thumbActive = Math.max(0, this._thumbActive - 1)
        this._drainThumbs()
      }
      img.addEventListener("load", done, { once: true })
      img.addEventListener("error", done, { once: true })
      this._materializeThumb(img)
    }
  }

  // Panel preview ONLY — refuse full 600 DPI extract / page_raster URLs.
  _isThumbUrl(url) {
    if (!url || typeof url !== "string") return false
    // Editor previews are resized whole pages, not cropped catalog thumbnails.
    if (this._isEditorPreviewUrl(url)) return false
    return /reference_region_thumb(?:\?|\/|$)/.test(url) || /[?&]max_edge=\d+/.test(url)
  }

  _isFullRasterUrl(url) {
    if (!url || typeof url !== "string") return false
    if (this._isThumbUrl(url)) return false
    return this._isEditorPreviewUrl(url) || /\/reference_region(?:\?|\/|$)/.test(url) || /\/page_raster(?:\?|\/|$)/.test(url)
  }

  _materializeThumb(img) {
    if (!img || img.dataset.loaded === "1") return
    const src = img.dataset.src || img.getAttribute("data-src")
    if (!src) return
    if (this._isFullRasterUrl(src)) {
      console.warn(
        "[cd-legend-crop] refused full-res URL on panel thumb — use reference_region_thumb",
        src
      )
      return
    }
    if (!this._isThumbUrl(src) && !src.startsWith("data:")) {
      // Unknown URL shape — still allow if explicitly not a full extract path.
      // Prefer being strict for known full endpoints only.
    }
    img.src = src
    img.dataset.loaded = "1"
    img.classList.remove("is-deferred")
    img.removeAttribute("data-src")
  }

  _isEditorPreviewUrl(url) {
    if (!url || typeof url !== "string") return false
    try {
      const parsed = new URL(url, window.location.origin)
      return /\/native_crop_pdf\/?$/.test(parsed.pathname)
    } catch (_error) {
      return false
    }
  }

  _fullRasterUrl() {
    const candidates = [
      this.pageRasterUrlValue,
      this._activePageCard?.dataset?.fullRasterUrl,
      this._activePageCard?.dataset?.pageRasterUrl
    ]
    return candidates.find((url) => this._isEditorPreviewUrl(url)) || ""
  }

  async _loadFullRasterOneAtATime(url) {
    const gen = ++this._fullRasterGen
    this._dropModalRaster()
    if (!this._isEditorPreviewUrl(url)) {
      this._setStatus("Native PDF unavailable. Refresh the legend view.")
      return
    }
    const abort = new AbortController()
    this._previewAbort = abort
    const deadline = setTimeout(() => {
      if (gen !== this._fullRasterGen) return
      this._fullRasterGen += 1
      this._dropModalRaster()
      this._setStatus("Native PDF load timed out. Reopen to retry.")
      this._syncSaveEnabled()
    }, 45000)
    try {
      const [response, renderer] = await Promise.all([
        fetch(url, { signal: abort.signal, credentials: "same-origin" }),
        import(this.imgTarget.dataset.pdfRendererUrl)
      ])
      if (!response.ok) throw new Error(`Native PDF failed (${response.status})`)
      const width = Number(response.headers.get("X-Page-Width"))
      const height = Number(response.headers.get("X-Page-Height"))
      if (!(width > 0 && height > 0)) throw new Error("Original crop coordinates unavailable")
      const bytes = await response.arrayBuffer()
      if (gen !== this._fullRasterGen || !this._modalOpen) return
      this.pageWidthValue = width
      this.pageHeightValue = height
      this._pdfLoading = renderer.openNativePage(bytes, this.imgTarget.dataset.pdfWorkerUrl)
      const document = await this._pdfLoading.promise
      const page = await document.getPage(1)
      if (gen !== this._fullRasterGen || !this._modalOpen) return
      this._pdfPage = page
      this._nativeScale = page.getViewport({ scale: 96 / 72 }).width / width
      await this._renderNativePage(gen)
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)))
      if (gen !== this._fullRasterGen || !this._modalOpen) return
      this.fitPage()
    } catch (error) {
      if (gen !== this._fullRasterGen || !this._modalOpen) return
      this._setStatus(error.message || "Failed to load native PDF")
    } finally {
      clearTimeout(deadline)
    }
  }

  _dropModalRaster() {
    this._previewAbort?.abort()
    this._pdfRenderTask?.cancel()
    this._pdfRenderTask = null
    this._pdfLoading?.destroy()?.catch(() => {})
    this._pdfLoading = null
    this._pdfPage = null
    this._imageReady = false
    if (this.hasImgTarget) {
      this.imgTarget.width = 1
      this.imgTarget.height = 1
    }
  }

  async _renderNativePage(gen) {
    if (!this._pdfPage || !this._modalOpen || gen !== this._fullRasterGen) return
    const { w, h } = this._pageSize()
    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    const base = this._pdfPage.getViewport({ scale: 1 })
    const maxEdge = 2200
    const fit = Math.min((96 / 72) * dpr, maxEdge / Math.max(base.width, base.height, 1))
    const viewport = this._pdfPage.getViewport({ scale: fit })
    const buffer = document.createElement("canvas")
    buffer.width = Math.ceil(viewport.width)
    buffer.height = Math.ceil(viewport.height)
    this._pdfRenderTask?.cancel()
    const task = this._pdfPage.render({ canvasContext: buffer.getContext("2d"), viewport })
    this._pdfRenderTask = task
    try {
      await task.promise
      if (gen !== this._fullRasterGen || task !== this._pdfRenderTask || !this._modalOpen) return
      const canvas = this.imgTarget
      canvas.width = buffer.width
      canvas.height = buffer.height
      Object.assign(canvas.style, { position: "absolute", left: "0px", top: "0px", width: `${w}px`, height: `${h}px` })
      canvas.getContext("2d").drawImage(buffer, 0, 0)
      this._imageReady = true
      this._syncSaveEnabled()
      this._setStatus(this._closed ? `Native PDF ready · polygon ${this._points.length} pts — Save crop` : "Native PDF ready — click vertices · zoom/pan · Enter to close")
    } catch (error) {
      if (error.name === "RenderingCancelledException" || gen !== this._fullRasterGen || task !== this._pdfRenderTask) return
      this._imageReady = false
      this._syncSaveEnabled()
      this._setStatus(`Native PDF render failed: ${error.message}`)
    }
  }

  _loadGeometry(bbox, poly) {
    this._points = []
    this._closed = false
    if (Array.isArray(poly) && poly.length >= 3) {
      this._points = poly
        .map((p) => ({
          x: Number(p.x ?? p[0]),
          y: Number(p.y ?? p[1])
        }))
        .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y))
      this._closed = this._points.length >= 3
    } else if (bbox && Number(bbox.width) > 0 && Number(bbox.height) > 0) {
      const b = bbox
      this._points = [
        { x: Number(b.x), y: Number(b.y) },
        { x: Number(b.x) + Number(b.width), y: Number(b.y) },
        { x: Number(b.x) + Number(b.width), y: Number(b.y) + Number(b.height) },
        { x: Number(b.x), y: Number(b.y) + Number(b.height) }
      ]
      this._closed = true
    }
  }

  onKeydown(event) {
    if (!this._modalOpen) return
    if (event.key === "Escape") {
      if (this._points.length && !this._closed) {
        event.preventDefault()
        this._points = []
        this._closed = false
        this._render()
        this._setStatus("Polygon cleared — click to place vertices.")
        this._syncSaveEnabled()
      } else {
        this.closeModal(event)
      }
      return
    }
    if (event.key === " " || event.code === "Space") {
      this._spaceDown = true
      if (this.hasStageTarget) this.stageTarget.classList.add("is-panning")
      event.preventDefault()
      return
    }
    if ((event.key === "Enter" || event.key === "e" || event.key === "E") && this._points.length >= 3) {
      event.preventDefault()
      this._closePolygon()
      return
    }
    if ((event.key === "Backspace" || event.key === "Delete") && this._points.length && !this._closed) {
      event.preventDefault()
      this._points.pop()
      this._render()
      this._setStatus(`${this._points.length} vertex · click to add, Enter to close`)
      this._syncSaveEnabled()
    }
  }

  onKeyup(event) {
    if (event.key === " " || event.code === "Space") {
      this._spaceDown = false
      this.stageTarget?.classList.remove("is-panning")
    }
  }

  startEdit(event) {
    event?.preventDefault?.()
    if (this.hasStageTarget) this.stageTarget.classList.add("is-editing")
    this._setStatus(
      this._closed
        ? `Polygon ${this._points.length} pts — pinch/wheel zoom · drag vertex · Save`
        : "Click vertices · pinch/wheel zoom · space+drag pan · Enter to close"
    )
    this._syncSaveEnabled()
  }

  // —— pointer / wheel ——
  pointerDown(event) {
    if (!this._modalOpen || !this.hasStageTarget || !this._imageReady) return
    if (event.button === 1 || event.button === 2 || this._spaceDown || event.altKey) {
      event.preventDefault()
      this._panning = true
      this._panStart = { x: event.clientX, y: event.clientY, panX: this._panX, panY: this._panY }
      this.stageTarget.classList.add("is-panning")
      return
    }
    if (event.button != null && event.button !== 0) return
    event.preventDefault()

    const pagePt = this._eventToPage(event)
    if (!pagePt) return

    // Drag existing vertex when closed
    if (this._closed) {
      const idx = this._hitVertex(pagePt, 14 / this._scale)
      if (idx >= 0) {
        this._dragVertex = idx
        return
      }
    }

    if (this._closed) {
      // Start a new polygon
      this._points = [pagePt]
      this._closed = false
      this._render()
      this._setStatus("1 vertex — click to continue polygon")
      this._syncSaveEnabled()
      return
    }

    // Close if near first point
    if (this._points.length >= 3) {
      const first = this._points[0]
      const dist = Math.hypot(pagePt.x - first.x, pagePt.y - first.y)
      if (dist < 18 / this._scale) {
        this._closePolygon()
        return
      }
    }

    this._points.push(pagePt)
    this._render()
    this._setStatus(
      this._points.length < 3
        ? `${this._points.length} vertex — need ≥3 · wheel to zoom`
        : `${this._points.length} vertices — click near start or Enter to close`
    )
    this._syncSaveEnabled()
  }

  pointerMove(event) {
    if (this._panning && this._panStart) {
      event.preventDefault()
      this._panX = this._panStart.panX + (event.clientX - this._panStart.x)
      this._panY = this._panStart.panY + (event.clientY - this._panStart.y)
      this._applyTransform()
      return
    }
    if (this._dragVertex >= 0) {
      event.preventDefault()
      const pagePt = this._eventToPage(event)
      if (!pagePt) return
      this._points[this._dragVertex] = this._clampPage(pagePt)
      this._render()
    }
  }

  pointerUp(event) {
    if (this._panning) {
      this._panning = false
      this._panStart = null
      if (!this._spaceDown) this.stageTarget?.classList.remove("is-panning")
    }
    if (this._dragVertex >= 0) {
      this._dragVertex = -1
      this._setStatus(`Polygon ${this._points.length} pts — Save crop`)
      this._syncSaveEnabled()
    }
  }

  wheel(event) {
    if (!this._modalOpen || !this.hasStageTarget) return
    event.preventDefault()
    const rect = this.stageTarget.getBoundingClientRect()
    const mx = event.clientX - rect.left
    const my = event.clientY - rect.top
    const factor = event.deltaY < 0 ? 1.12 : 1 / 1.12
    this._zoomAt(mx, my, factor)
  }

  // Two-finger pinch zoom (touch).
  _bindPinch() {
    if (!this.hasStageTarget) return
    this._onPinchStart = (e) => this._pinchStart(e)
    this._onPinchMove = (e) => this._pinchMove(e)
    this._onPinchEnd = (e) => this._pinchEnd(e)
    this.stageTarget.addEventListener("touchstart", this._onPinchStart, { passive: false })
    this.stageTarget.addEventListener("touchmove", this._onPinchMove, { passive: false })
    this.stageTarget.addEventListener("touchend", this._onPinchEnd)
    this.stageTarget.addEventListener("touchcancel", this._onPinchEnd)
  }

  _unbindPinch() {
    if (!this.hasStageTarget) return
    if (this._onPinchStart) this.stageTarget.removeEventListener("touchstart", this._onPinchStart)
    if (this._onPinchMove) this.stageTarget.removeEventListener("touchmove", this._onPinchMove)
    if (this._onPinchEnd) this.stageTarget.removeEventListener("touchend", this._onPinchEnd)
    if (this._onPinchEnd) this.stageTarget.removeEventListener("touchcancel", this._onPinchEnd)
  }

  _touchDist(touches) {
    const a = touches[0]
    const b = touches[1]
    return Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY)
  }

  _touchMid(touches, rect) {
    const a = touches[0]
    const b = touches[1]
    return {
      x: (a.clientX + b.clientX) / 2 - rect.left,
      y: (a.clientY + b.clientY) / 2 - rect.top
    }
  }

  _pinchStart(event) {
    if (!this._modalOpen || event.touches.length !== 2) return
    event.preventDefault()
    const rect = this.stageTarget.getBoundingClientRect()
    const mid = this._touchMid(event.touches, rect)
    this._pinch = {
      dist: this._touchDist(event.touches),
      scale: this._scale,
      midX: mid.x,
      midY: mid.y,
      panX: this._panX,
      panY: this._panY
    }
    this._dragging = false
    this._dragVertex = -1
  }

  _pinchMove(event) {
    if (!this._pinch || event.touches.length !== 2) return
    event.preventDefault()
    const dist = this._touchDist(event.touches)
    if (this._pinch.dist < 8) return
    const factor = dist / this._pinch.dist
    const next = Math.min(
      this._maxScale(),
      Math.max(this._minScale(), this._pinch.scale * factor)
    )
    const rect = this.stageTarget.getBoundingClientRect()
    const mid = this._touchMid(event.touches, rect)
    // Zoom around original pinch midpoint in world space, then re-anchor to live mid.
    const worldX = (this._pinch.midX - this._pinch.panX) / this._pinch.scale
    const worldY = (this._pinch.midY - this._pinch.panY) / this._pinch.scale
    this._scale = next
    this._panX = mid.x - worldX * this._scale
    this._panY = mid.y - worldY * this._scale
    this._applyTransform()
    this._updateZoomLabel()
  }

  _pinchEnd(event) {
    if (event.touches && event.touches.length >= 2) return
    this._pinch = null
  }

  _zoomAt(mx, my, factor) {
    const next = Math.min(this._maxScale(), Math.max(this._minScale(), this._scale * factor))
    const worldX = (mx - this._panX) / this._scale
    const worldY = (my - this._panY) / this._scale
    this._scale = next
    this._panX = mx - worldX * this._scale
    this._panY = my - worldY * this._scale
    this._applyTransform()
    this._updateZoomLabel()
  }

  undoVertex(event) {
    event?.preventDefault?.()
    if (this._closed) {
      this._closed = false
      this._setStatus("Polygon reopened — edit vertices or add more")
    } else if (this._points.length) {
      this._points.pop()
    }
    this._render()
    this._syncSaveEnabled()
  }

  resetCrop(event) {
    event?.preventDefault?.()
    this._points = []
    this._closed = false
    this._render()
    this._setStatus("Polygon cleared — click vertices to crop the legend region")
    this._syncSaveEnabled()
  }

  async save(event) {
    event?.preventDefault?.()
    if (this._saving || !this.updateUrlValue || !this._imageReady) return
    const box = this._aabb()
    if (!box || this._points.length < 3) {
      this._setStatus("Need a closed polygon (≥3 points) before save.")
      return
    }
    if (!this._closed) this._closePolygon()

    this._saving = true
    this._syncSaveEnabled()
    this._setStatus("Saving crop…")

    const token =
      document.querySelector('meta[name="csrf-token"]')?.content ||
      document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")

    const body = new FormData()
    body.append("page_number", String(this.pageValue))
    body.append("crop_id", this._cropId || "new")
    body.append("bbox[x]", String(box.x))
    body.append("bbox[y]", String(box.y))
    body.append("bbox[width]", String(box.width))
    body.append("bbox[height]", String(box.height))
    this._points.forEach((p, i) => {
      body.append(`polygon[${i}][x]`, String(p.x))
      body.append(`polygon[${i}][y]`, String(p.y))
    })
    body.append("skip_reparse", "1")

    try {
      const res = await fetch(this.updateUrlValue, {
        method: "PATCH",
        headers: {
          Accept: "text/vnd.turbo-stream.html, application/json",
          "X-CSRF-Token": token || "",
          "X-Requested-With": "XMLHttpRequest"
        },
        body,
        credentials: "same-origin"
      })
      const ct = res.headers.get("content-type") || ""
      if (!res.ok) {
        const body = await res.text()
        let message = `Crop save failed (HTTP ${res.status})`
        try {
          const data = JSON.parse(body)
          if (data.error) message = data.error
        } catch (_err) {
          /* keep HTTP status message */
        }
        throw new Error(message)
      }
      if (ct.includes("turbo-stream") || ct.includes("text/vnd.turbo-stream")) {
        const html = await res.text()
        // A queued pre-save catalog must not overwrite the committed crop.
        this._deferredCatalogStream = null
        this.closeModal()
        Turbo.renderStreamMessage(html)
        return
      }
      const data = await res.json().catch(() => ({}))
      if (!res.ok || data.ok !== true) throw new Error(data.error || `HTTP ${res.status}`)
      this._setStatus(data.message || "Crop saved.")
      // Refresh PANEL thumb only — never full reference_region_url (25k×18k).
      if (this._activePageCard) {
        this._activePageCard.dataset.existingBbox = JSON.stringify(data.bbox || box)
        this._activePageCard.dataset.existingPolygon = JSON.stringify(this._points)
        this._activePageCard.querySelectorAll("[data-crop-id]").forEach((button) => {
          if (button.dataset.cropId !== this._cropId) return
          button.dataset.existingBbox = JSON.stringify(data.bbox || box)
          button.dataset.existingPolygon = JSON.stringify(this._points)
        })
        const thumbUrl =
          data.reference_region_thumb_url ||
          this._activePageCard.dataset.thumbUrl ||
          null
        const thumb = this._activePageCard.querySelector("img.cd-catalog-preview-img")
        if (thumb && thumbUrl && this._isThumbUrl(thumbUrl)) {
          const bust = `${thumbUrl}${thumbUrl.includes("?") ? "&" : "?"}t=${Date.now()}`
          thumb.src = bust
          thumb.dataset.src = thumbUrl
          thumb.dataset.loaded = "1"
          thumb.classList.remove("is-deferred")
        } else if (thumb && data.reference_region_url) {
          console.warn(
            "[cd-legend-crop] save returned full reference_region_url without thumb — skip panel paint"
          )
        }
      }
      this.closeModal()
    } catch (err) {
      console.warn("[cd-legend-crop] save failed", err)
      this._setStatus(`Failed: ${err.message}`)
    } finally {
      this._saving = false
      this._syncSaveEnabled()
    }
  }

  // —— helpers ——
  _bindWindow() {
    this._onWinMove = (e) => this.pointerMove(e)
    this._onWinUp = (e) => this.pointerUp(e)
    this._onWinKeyUp = (e) => this.onKeyup(e)
    window.addEventListener("mousemove", this._onWinMove)
    window.addEventListener("mouseup", this._onWinUp)
    window.addEventListener("keyup", this._onWinKeyUp)
  }

  _unbindWindow() {
    if (this._onWinMove) window.removeEventListener("mousemove", this._onWinMove)
    if (this._onWinUp) window.removeEventListener("mouseup", this._onWinUp)
    if (this._onWinKeyUp) window.removeEventListener("keyup", this._onWinKeyUp)
  }

  _unlockBody() {
    document.body.classList.remove("cd-legend-modal-open")
  }

  _unbindBody() {
    this._unlockBody()
  }

  _pageSize() {
    return { w: this.pageWidthValue || 1, h: this.pageHeightValue || 1 }
  }

  // Smallest zoom that still lets the entire page fit with a little margin.
  _fitScale() {
    if (!this.hasStageTarget) return 0.05
    const stage = { width: this.stageTarget.clientWidth, height: this.stageTarget.clientHeight }
    const { w, h } = this._pageSize()
    if (stage.width < 10 || stage.height < 10 || w < 1 || h < 1) return 0.05
    return Math.min(stage.width / w, stage.height / h) * 0.96
  }

  // Allow zooming out past fit so the whole sheet always remains reachable.
  _minScale() {
    return Math.max(0.0001, this._fitScale() * 0.35)
  }

  _maxScale() {
    return 24
  }

  actualSize(event) {
    event?.preventDefault?.()
    this._scale = this._nativeScale || 1
    this._panX = 0
    this._panY = 0
    this._applyTransform()
    this._updateZoomLabel()
    this._render()
  }

  // Fit after the canvas has its final dimensions and the modal has laid out.
  _fitToView() {
    if (!this.hasStageTarget || !this.hasImgTarget || !this.pageWidthValue || !this.pageHeightValue) return
    const stage = { width: this.stageTarget.clientWidth, height: this.stageTarget.clientHeight }
    const { w: natW, h: natH } = this._pageSize()
    if (stage.width < 10 || stage.height < 10) return
    // No 15% floor — large 600 DPI sheets often need ~5–12% to show whole page.
    this._scale = Math.min(this._maxScale(), Math.max(0.0001, this._fitScale()))
    this._panX = (stage.width - natW * this._scale) / 2
    this._panY = (stage.height - natH * this._scale) / 2
    this._applyTransform()
    this._updateZoomLabel()
  }

  fitPage(event) {
    event?.preventDefault?.()
    this._fitToView()
    this._render()
    this._setStatus("Full page fit — entire document in view")
  }

  _applyTransform() {
    if (!this.hasViewportTarget) return
    this.viewportTarget.style.transform = `translate(${this._panX}px, ${this._panY}px) scale(${this._scale})`
    this.viewportTarget.style.transformOrigin = "0 0"
  }

  _updateZoomLabel() {
    if (this.hasZoomLabelTarget) {
      this.zoomLabelTarget.textContent = `${Math.round(this._scale / (this._nativeScale || 1) * 100)}%`
    }
  }

  _clampPage(p) {
    const { w, h } = this._pageSize()
    return {
      x: Math.min(Math.max(0, p.x), w),
      y: Math.min(Math.max(0, p.y), h)
    }
  }

  _eventToPage(event) {
    if (!this.hasStageTarget || !this.hasImgTarget) return null
    const stage = this.stageTarget.getBoundingClientRect()
    const touch = event.touches?.[0] || event.changedTouches?.[0]
    const clientX = event.clientX ?? touch?.clientX
    const clientY = event.clientY ?? touch?.clientY
    if (clientX == null) return null
    const sx = clientX - stage.left
    const sy = clientY - stage.top
    const pageX = (sx - this._panX) / this._scale
    const pageY = (sy - this._panY) / this._scale
    return this._clampPage({ x: pageX, y: pageY })
  }

  _hitVertex(pagePt, radius) {
    for (let i = 0; i < this._points.length; i++) {
      const p = this._points[i]
      if (Math.hypot(pagePt.x - p.x, pagePt.y - p.y) <= radius) return i
    }
    return -1
  }

  _closePolygon() {
    if (this._points.length < 3) {
      this._setStatus("Need at least 3 vertices.")
      return
    }
    this._closed = true
    this._render()
    const b = this._aabb()
    this._setStatus(
      `Closed polygon · ${this._points.length} pts · ${Math.round(b.width)}×${Math.round(b.height)} px — Save crop`
    )
    this._syncSaveEnabled()
  }

  _aabb() {
    if (this._points.length < 2) return null
    let minX = Infinity
    let minY = Infinity
    let maxX = -Infinity
    let maxY = -Infinity
    this._points.forEach((p) => {
      minX = Math.min(minX, p.x)
      minY = Math.min(minY, p.y)
      maxX = Math.max(maxX, p.x)
      maxY = Math.max(maxY, p.y)
    })
    const width = maxX - minX
    const height = maxY - minY
    if (width < 4 || height < 4) return null
    return { x: minX, y: minY, width, height, units: "px" }
  }

  _render() {
    const { w, h } = this._pageSize()
    if (this.hasSvgTarget) {
      this.svgTarget.setAttribute("viewBox", `0 0 ${w} ${h}`)
      this.svgTarget.setAttribute("width", String(w))
      this.svgTarget.setAttribute("height", String(h))
    }

    if (this.hasViewportTarget) {
      this.viewportTarget.style.width = `${w}px`
      this.viewportTarget.style.height = `${h}px`
    }

    const pts = this._points
    const pathD =
      pts.length === 0
        ? ""
        : pts.map((p, i) => `${i === 0 ? "M" : "L"}${p.x} ${p.y}`).join(" ") +
          (this._closed ? " Z" : "")

    if (this.hasPolyPathTarget) {
      this.polyPathTarget.setAttribute("d", pathD)
      this.polyPathTarget.style.display = pts.length ? "" : "none"
    }
    if (this.hasPolyLineTarget) {
      this.polyLineTarget.setAttribute("d", pathD)
      this.polyLineTarget.style.display = pts.length ? "" : "none"
    }
    if (this.hasPolyDotsTarget) {
      this.polyDotsTarget.innerHTML = ""
      pts.forEach((p, i) => {
        const c = document.createElementNS("http://www.w3.org/2000/svg", "circle")
        c.setAttribute("cx", String(p.x))
        c.setAttribute("cy", String(p.y))
        c.setAttribute("r", String(Math.max(4, 6 / this._scale)))
        c.setAttribute("class", i === 0 && !this._closed ? "is-first" : "")
        this.polyDotsTarget.appendChild(c)
      })
    }
  }

  _setStatus(msg) {
    if (this.hasStatusTarget) this.statusTarget.textContent = msg
  }

  _syncSaveEnabled() {
    if (!this.hasSaveBtnTarget) return
    this.saveBtnTarget.disabled = this._saving || !(this._imageReady && this._closed && this._points.length >= 3 && this._aabb())
  }
}
